import { useQuery } from '@apollo/client';
import { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes.ts';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { GetDealsListBasicDocument } from '../../../../../../graphql/query/deals/GetDealsListBasic.generated.tsx';
import { useDealsUsedByAudiences } from '../../../../hooks/useDealsUsedByAudiences.ts';

type UseDealsRevenueMethodResponse = {
  /**
   * Indicates if any deal has PoM as a revenue method.
   */
  havePoM: boolean;
  isLoading: boolean;
};

export const useDealsRevenueMethod = (): UseDealsRevenueMethodResponse => {
  const { audienceUid = '' } = useParams<{ audienceUid: string }>();

  const { error, loading, deals } = useDealsUsedByAudiences([audienceUid], !audienceUid);

  const firstRequestIsDoneAndHaveData = !loading && !error && (deals.length ?? 0) > 0;

  const queryString = `exchange_id__in=${deals.map(({ id }) => id).join(',')}`;

  const { data, loading: dealsLoading } = useQuery(GetDealsListBasicDocument, {
    skip: !firstRequestIsDoneAndHaveData,
    variables: { queryString },
  });

  return useMemo(() => {
    const dealsToCheck = data?.deals?.objects ?? [];
    const havePoM = dealsToCheck.some(
      ({ exchange_deal }) => exchange_deal?.segment_partner_revenue_method === SegmentPartnerRevenueMethod.PoM
    );

    return {
      isLoading: loading || dealsLoading,
      havePoM,
    };
  }, [data, loading, dealsLoading]);
};
